import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  numeric?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, leftAddon, rightAddon, numeric, className, id, ...rest }, ref) => {
    const reactId = useId();
    const inputId = id ?? `input-${reactId}`;
    const describedById = hint || error ? `${inputId}-desc` : undefined;

    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-ink">
            {label}
          </label>
        )}

        <div
          className={cn(
            'flex items-stretch rounded-btn border bg-white overflow-hidden',
            'focus-within:ring-2 focus-within:ring-accent-2 focus-within:border-accent',
            error ? 'border-red' : 'border-line',
          )}
        >
          {leftAddon && (
            <span className="flex items-center px-3 bg-paper-2 text-muted text-sm border-r border-line">
              {leftAddon}
            </span>
          )}
          <input
            id={inputId}
            ref={ref}
            aria-invalid={!!error}
            aria-describedby={describedById}
            inputMode={numeric ? 'decimal' : rest.inputMode}
            className={cn(
              'flex-1 min-h-tap px-3 py-2 outline-none bg-transparent text-ink placeholder:text-muted',
              numeric && 'font-mono tnum',
              className,
            )}
            {...rest}
          />
          {rightAddon && (
            <span className="flex items-center px-3 bg-paper-2 text-muted text-sm border-l border-line">
              {rightAddon}
            </span>
          )}
        </div>

        {(hint || error) && (
          <p
            id={describedById}
            className={cn('text-xs', error ? 'text-red' : 'text-muted')}
          >
            {error ?? hint}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';
